/**
 * The verifier modal — domino's `verify/VerifierModal.ts` ported to mahjong's
 * one-proof-per-hand shape and mahjong's DOM idiom (a `@bn-games/ui-shell`
 * modal with inline rem styles, as `ui/HandEndModal.ts` does, rather than
 * domino's app stylesheet).
 *
 * Domino verifies a whole match at once, so its modal is a list of per-hand
 * rows. Here the entry point is the hand-end modal of the hand that just
 * finished, so there is exactly one proof on screen and the "list" collapses to
 * a single status line under the provenance rows — see `verifyModel.ts` for why
 * a mahjong deal can only ever be verified once its hand is over.
 *
 * The BLS check itself is `@bn-games/verifiable-rng`'s, which lazy-imports the
 * curve module; opening this modal is the only thing in the app that pulls it
 * into the bundle.
 */

import { el, openModal } from '@bn-games/ui-shell';
import {
  DRAND_DISPLAY_NAME,
  DRAND_SIGNER_LABEL,
  verifyMahjongSeedProof,
} from '@bn-games/verifiable-rng';
import { t } from '../i18n/I18n.js';
import {
  verifyDetails,
  verifyView,
  type VerifiableDeal,
  type VerifyDetail,
  type VerifyStatus,
  type VerifyTone,
} from './verifyModel.js';

const SOURCE = { displayName: DRAND_DISPLAY_NAME, signerLabel: DRAND_SIGNER_LABEL };

const BODY_STYLE = ['display:flex', 'flex-direction:column', 'gap:0.75rem'].join(';');
const EXPLAINER_STYLE = 'margin:0;font-size:0.9rem;line-height:1.4;opacity:0.85';
const LIST_STYLE = ['display:flex', 'flex-direction:column', 'gap:0.25rem', 'margin:0'].join(';');
const ROW_STYLE = [
  'display:flex',
  'justify-content:space-between',
  'gap:1rem',
  'font-size:0.9rem',
].join(';');
const KEY_STYLE = 'opacity:0.7';
const VALUE_STYLE = 'font-family:ui-monospace,monospace;text-align:right;word-break:break-all';
const STATUS_STYLE = [
  'display:flex',
  'align-items:center',
  'gap:0.5rem',
  'padding-top:0.5rem',
  'font-size:1rem',
  'font-weight:600',
].join(';');

const TONE_COLORS: Readonly<Record<VerifyTone, string>> = {
  ok: '#7fdca4',
  bad: '#e2695f',
  warn: '#f0c860',
  pending: 'inherit',
};

function detailRow(detail: VerifyDetail): HTMLElement {
  const value = el('span', {
    text: detail.value,
    attrs: detail.full === undefined
      ? { style: VALUE_STYLE }
      : { style: VALUE_STYLE, title: detail.full },
  });
  return el('div', { attrs: { style: ROW_STYLE } }, [
    el('span', { text: t(detail.labelKey), attrs: { style: KEY_STYLE } }),
    value,
  ]);
}

function detailList(deal: VerifiableDeal, derivedSeed: number | null): HTMLElement {
  return el(
    'div',
    { attrs: { style: LIST_STYLE } },
    verifyDetails(deal, SOURCE, derivedSeed).map(detailRow),
  );
}

function statusLine(status: VerifyStatus): HTMLElement {
  const view = verifyView(status);
  return el('div', { attrs: { style: `${STATUS_STYLE};color:${TONE_COLORS[view.tone]}` } }, [
    el('span', { text: view.glyph }),
    el('span', { text: t(view.labelKey) }),
  ]);
}

function bodyFor(deal: VerifiableDeal, status: VerifyStatus, derivedSeed: number | null): HTMLElement {
  return el('div', { attrs: { style: BODY_STYLE } }, [
    el('p', { text: t('verify.explainer'), attrs: { style: EXPLAINER_STYLE } }),
    detailList(deal, derivedSeed),
    statusLine(status),
  ]);
}

/**
 * Open the modal and start the check. The body is rebuilt rather than patched
 * in place when the verdict lands — it is three short rows and one status line,
 * so there is nothing worth the bookkeeping of a mutable row model.
 */
export function openVerifierModal(deal: VerifiableDeal): void {
  const handle = openModal({
    title: t('verify.title'),
    body: bodyFor(deal, 'pending', null),
    actions: [{ label: t('verify.close'), primary: true, onClick: () => handle.close() }],
  });
  void verifyMahjongSeedProof(deal.proof, deal.roomCode, deal.seed).then((result) => {
    handle.setBody(bodyFor(deal, result.status, result.derivedSeed ?? null));
  });
}
